import React, { useEffect, useCallback, useState, useRef } from 'react';

import { useToasts } from 'react-toast-notifications';
import { Link, useParams, useHistory } from 'react-router-dom';
import ReactSelect from 'react-select';

import { makeStyles } from '@material-ui/core/styles';
import {
  CircularProgress,
  IconButton,
  Table as MuiTable,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from '@material-ui/core';
import Box from '@material-ui/core/Box';

import CurrencyTextField from '@unicef/material-ui-currency-textfield';
import Delete from '@material-ui/icons/Delete';
import Select from '@material-ui/core/Select';
import FormControl from '@material-ui/core/FormControl';
import InputLabel from '@material-ui/core/InputLabel';
import MenuItem from '@material-ui/core/MenuItem';

import styles from 'assets/jss/material-dashboard-react/views/dashboardStyle.js';
import GridItem from 'components/Grid/GridItem.js';
import GridContainer from 'components/Grid/GridContainer.js';
import TextField from '@material-ui/core/TextField';
import Button from 'components/CustomButtons/Button.js';
import Card from 'components/Card/Card.js';
import CardHeader from 'components/Card/CardHeader.js';
import CardBody from 'components/Card/CardBody.js';
import CardFooter from 'components/Card/CardFooter.js';

import api from '../../services/api';

const useStyles = makeStyles(styles);

const CANCELLED_STATUS_ID = '95d754a7-2198-4bfd-a242-e3575137b3e2';
const REQUIRED_ADDRESS_FIELDS = [
  ['identificacao', 'a identificação do endereço'],
  ['cep', 'o CEP de entrega'],
  ['endereco', 'o endereço de entrega'],
  ['numero', 'o número do endereço'],
  ['bairro', 'o bairro de entrega'],
  ['cidade', 'a cidade de entrega'],
  ['estado', 'o estado de entrega'],
];

const selectStyles = {
  control: base => ({
    ...base,
    minHeight: 56,
  }),
  menuPortal: base => ({
    ...base,
    zIndex: 9999,
  }),
};

function formatCurrency(value) {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(Number(value || 0));
}

function buildProductLabel(code, name) {
  return `COD: ${code || '-'} - ${name || 'Produto sem nome'}`;
}

function normalizeDecimalValue(value) {
  if (value === null || value === undefined || value === '') {
    return 0;
  }

  if (typeof value === 'number') {
    return value;
  }

  return Number(String(value).replace(',', '.'));
}

function normalizeQuantityInput(value) {
  if (value === null || value === undefined || value === '') {
    return '';
  }

  if (typeof value === 'number') {
    return String(value);
  }

  return String(value).replace(',', '.');
}

function createItemKey() {
  return `item-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

function buildEditableAddress(address = {}) {
  return {
    ...address,
    identificacao: address?.identificacao || '',
    endereco: address?.endereco || '',
    numero: address?.numero || '',
    complemento: address?.complemento || '',
    bairro: address?.bairro || '',
    cidade: address?.cidade || '',
    estado: address?.estado || '',
    cep: address?.cep || '',
  };
}

function getOrderItemQuantity(item) {
  return normalizeDecimalValue(item?.qtde ?? item?.quantidade);
}

function getOrderItemUnitValue(item) {
  return Number((item?.unitValue ?? item?.value ?? item?.valor) || 0);
}

function buildOrderUpdateItem(item) {
  return {
    ...(item?.id ? { id: item.id } : {}),
    product_id: item?.productId || item?.product?.id || item?.product_id,
    color_id: item?.colorId || item?.color?.id || item?.color_id,
    qtde: getOrderItemQuantity(item),
    value: getOrderItemUnitValue(item),
    discount: Number(
      (item?.discountValue ?? item?.discount ?? item?.desconto) || 0,
    ),
    coupon_id: item?.couponId ?? item?.coupon?.id ?? item?.coupon_id ?? null,
  };
}

function getItemsSubtotalValue(orderItems = []) {
  return orderItems.reduce((acc, item) => {
    return acc + getOrderItemQuantity(item) * getOrderItemUnitValue(item);
  }, 0);
}

export default function EditOrder() {
  const [status, setStatus] = useState();
  const [loading, setLoading] = useState(true);
  const [loadingEtiqueta, setLoadingEtiqueta] = useState(false);
  const [loadingSave, setLoadingSave] = useState(false);
  const [url, setUrl] = useState();
  const [nf, setNF] = useState('');
  const [data, setData] = useState();
  const [dataStatus, setDataStatus] = useState();
  const [total, setTotal] = useState('R$ 0,00');
  const [itens, setItens] = useState([]);
  const [cancelReason, setCancelReason] = useState('');
  const [cancelReasonError, setCancelReasonError] = useState(false);
  const [melhorEnvioConnected, setMelhorEnvioConnected] = useState(null);
  const [loadingMelhorEnvioStatus, setLoadingMelhorEnvioStatus] = useState(
    false,
  );
  const [productOptions, setProductOptions] = useState([]);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [selectedProductColors, setSelectedProductColors] = useState([]);
  const [selectedColorId, setSelectedColorId] = useState('');
  const [selectedQuantity, setSelectedQuantity] = useState('1');
  const [selectedUnitValue, setSelectedUnitValue] = useState(0);
  const [loadingProductDetails, setLoadingProductDetails] = useState(false);
  const [editingAddress, setEditingAddress] = useState(false);
  const [loadingAddressSave, setLoadingAddressSave] = useState(false);
  const [addressDraft, setAddressDraft] = useState(buildEditableAddress());

  const [trackingUrl, setTrackingUrl] = useState('');

  const productCacheRef = useRef({});
  const history = useHistory();
  const { addToast } = useToasts();

  const { id } = useParams();
  const classes = useStyles();
  const melhorEnvioOAuthBaseUrl = process.env.REACT_APP_API_URL
    ? `${process.env.REACT_APP_API_URL.replace(
        /\/$/,
        '',
      )}/melhorenvio/oauth/authorize`
    : '';

  const getOrderTotal = useCallback((orderItems, orderData) => {
    const itemsTotal = orderItems.reduce((acc, item) => {
      return (
        acc + normalizeDecimalValue(item.qtde) * Number(item.unitValue || 0)
      );
    }, 0);

    return formatCurrency(
      itemsTotal +
        Number(orderData?.freteValor || 0) -
        Number(orderData?.valordesconto || 0),
    );
  }, []);

  const extractProductColors = useCallback(productData => {
    const colors = [];

    if (Array.isArray(productData?.colors)) {
      productData.colors.forEach(color => {
        if (color?.id && !colors.find(item => item.id === color.id)) {
          colors.push({
            id: color.id,
            name: color.name,
            color: color.color,
          });
        }
      });
    }

    if (colors.length > 0) {
      return colors;
    }

    if (Array.isArray(productData?.images)) {
      productData.images.forEach(image => {
        const color = image?.color;

        if (color?.id && !colors.find(item => item.id === color.id)) {
          colors.push({
            id: color.id,
            name: color.name,
            color: color.color,
          });
        }
      });
    }

    return colors;
  }, []);

  const fetchProductDetails = useCallback(
    async productId => {
      if (!productId) {
        return null;
      }

      if (productCacheRef.current[productId]) {
        return productCacheRef.current[productId];
      }

      const response = await api.get(`/products/${productId}`);
      const productData = response.data;
      const productInfo = {
        id: productData.id || productId,
        code: productData.codigo,
        name: productData.nome,
        unitValue: Number(productData.valor || 0),
        colorOptions: extractProductColors(productData),
      };

      productCacheRef.current[productId] = productInfo;

      return productInfo;
    },
    [extractProductColors],
  );

  const getPaymentStatusOptions = useCallback(async () => {
    try {
      const response = await api.get(`/orders/paymentstatus`);
      setDataStatus(response.data.status_options);
    } catch (e) {}
  }, []);

  const getMelhorEnvioConnectionValue = useCallback(responseData => {
    if (typeof responseData === 'boolean') {
      return responseData;
    }

    if (typeof responseData?.connected === 'boolean') {
      return responseData.connected;
    }

    if (typeof responseData?.conectado === 'boolean') {
      return responseData.conectado;
    }

    if (typeof responseData?.authorized === 'boolean') {
      return responseData.authorized;
    }

    if (typeof responseData?.autorizado === 'boolean') {
      return responseData.autorizado;
    }

    if (typeof responseData?.isConnected === 'boolean') {
      return responseData.isConnected;
    }

    if (typeof responseData?.status === 'string') {
      const normalizedStatus = responseData.status.toLowerCase();

      if (
        [
          'connected',
          'conectado',
          'authorized',
          'autorizado',
          'active',
          'ativo',
        ].includes(normalizedStatus)
      ) {
        return true;
      }

      if (
        [
          'disconnected',
          'desconectado',
          'unauthorized',
          'nao_autorizado',
          'não_autorizado',
          'inactive',
          'inativo',
        ].includes(normalizedStatus)
      ) {
        return false;
      }
    }

    return false;
  }, []);

  const getMelhorEnvioStatus = useCallback(async () => {
    try {
      setLoadingMelhorEnvioStatus(true);

      const response = await api.get('/melhorenvio/status');

      setMelhorEnvioConnected(getMelhorEnvioConnectionValue(response.data));
    } catch (e) {
      setMelhorEnvioConnected(false);
    } finally {
      setLoadingMelhorEnvioStatus(false);
    }
  }, [getMelhorEnvioConnectionValue]);

  const loadProductOptions = useCallback(async () => {
    try {
      setLoadingProducts(true);

      const response = await api.post('/products/search', {
        filtros: {
          ativo: true,
          destaque: 'T',
          category_id: 'T',
          subcategory_id: 'T',
          codigo: '',
          codigoErp: '',
          nome: '',
        },
      });

      const options = response.data.map(product => ({
        value: product.id,
        label: buildProductLabel(product.codigo, product.nome),
        code: product.codigo,
        name: product.nome,
        unitValue: Number(product.valor || 0),
      }));

      setProductOptions(options);
    } catch (e) {
      addToast('Não foi possível carregar os produtos para editar o pedido.', {
        appearance: 'error',
        autoDismiss: true,
      });
    } finally {
      setLoadingProducts(false);
    }
  }, [addToast]);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);

      const response = await api.get(`/orders/${id}`);
      const orderData = {
        ...response.data,
        address: buildEditableAddress(response.data.address),
      };

      setData(orderData);
      setStatus(orderData.status_id);
      setCancelReason(orderData.cancel_reason || '');
      setUrl(orderData.url);
      setNF(orderData.nf || '');
      setAddressDraft(orderData.address);
      setEditingAddress(false);

      let tracking_code = orderData.melhorenvio_tracking;
      let tracking_url = orderData.melhorenvio_tracking_url;

      console.log('Tracking code:', tracking_code);
      console.log('Tracking URL from API:', tracking_url);

      if (tracking_code) {
        tracking_url = `https://melhorrastreio.com.br/app/melhorenvio/${tracking_code}`;
      }
      if (tracking_url) {
        tracking_url = tracking_url;
      }

      console.log(tracking_url);

      setTrackingUrl(tracking_url);

      const currentItems = (orderData.itens || []).map(item => {
        const currentColor = item.color?.id
          ? {
              id: item.color.id,
              name: item.color.name,
              color: item.color.color,
            }
          : null;
        const baseColorOptions = extractProductColors(item.product);
        const colorOptions =
          currentColor &&
          !baseColorOptions.find(color => color.id === currentColor.id)
            ? [...baseColorOptions, currentColor]
            : baseColorOptions;

        return {
          key: item.id || createItemKey(),
          id: item.id,
          productId: item.product?.id || item.product_id,
          productCode: item.product?.codigo || '',
          productName: item.product?.nome || '',
          productLabel: buildProductLabel(
            item.product?.codigo,
            item.product?.nome,
          ),
          colorId: item.color?.id || item.color_id || '',
          colorName: item.color?.name || '',
          colorHex: item.color?.color || '',
          colorOptions,
          qtde: normalizeQuantityInput(item.qtde),
          unitValue: Number(item.value || 0),
          discountValue: Number(item.discount || 0),
          couponId: item.coupon?.id || null,
          couponCode: item.coupon?.code || '-x-',
        };
      });

      const uniqueProductIds = Array.from(
        new Set(currentItems.map(item => item.productId).filter(Boolean)),
      );

      const productsInfo = await Promise.all(
        uniqueProductIds.map(async productId => {
          try {
            return [productId, await fetchProductDetails(productId)];
          } catch (e) {
            return [productId, null];
          }
        }),
      );

      const productInfoMap = Object.fromEntries(productsInfo);

      const hydratedItems = currentItems.map(item => {
        const productInfo = productInfoMap[item.productId];

        if (!productInfo) {
          return item;
        }

        const currentColorOption =
          item.colorId && item.colorName
            ? {
                id: item.colorId,
                name: item.colorName,
                color: item.colorHex,
              }
            : null;

        let colorOptions =
          productInfo.colorOptions && productInfo.colorOptions.length > 0
            ? productInfo.colorOptions
            : item.colorOptions;

        if (
          currentColorOption &&
          !colorOptions.find(color => color.id === currentColorOption.id)
        ) {
          colorOptions = [...colorOptions, currentColorOption];
        }

        return {
          ...item,
          productCode: productInfo.code || item.productCode,
          productName: productInfo.name || item.productName,
          productLabel: buildProductLabel(
            productInfo.code || item.productCode,
            productInfo.name || item.productName,
          ),
          colorOptions,
        };
      });

      setItens(hydratedItems);
      setTotal(getOrderTotal(hydratedItems, orderData));
    } catch (e) {
      addToast(e.response ? e.response.data.message : 'Ocorreu um erro !', {
        appearance: 'error',
        autoDismiss: true,
      });

      history.push('/admin/orders');
    } finally {
      setLoading(false);
    }
  }, [
    addToast,
    extractProductColors,
    fetchProductDetails,
    getOrderTotal,
    history,
    id,
  ]);

  useEffect(() => {
    getPaymentStatusOptions();
    loadData();
    getMelhorEnvioStatus();
    loadProductOptions();
  }, [
    getMelhorEnvioStatus,
    getPaymentStatusOptions,
    loadData,
    loadProductOptions,
  ]);

  useEffect(() => {
    if (data) {
      setTotal(getOrderTotal(itens, data));
    }
  }, [data, getOrderTotal, itens]);

  const autenticarMelhorEnvio = useCallback(async () => {
    if (!melhorEnvioOAuthBaseUrl) {
      addToast(
        'Configure a REACT_APP_API_URL para acessar a rota de reautenticacao do Melhor Envio.',
        {
          appearance: 'error',
          autoDismiss: true,
        },
      );

      return;
    }

    const { data } = await api.get(
      '/melhorenvio/oauth/authorize?redirect=false',
    );
    window.location.href = data.url;
  }, [addToast, melhorEnvioOAuthBaseUrl]);

  const gerarEtiqueta = useCallback(
    async volumes => {
      if (editingAddress) {
        addToast(
          'Salve ou cancele a edição do endereço antes de gerar a etiqueta.',
          {
            appearance: 'error',
            autoDismiss: true,
          },
        );
        return;
      }

      if (status === CANCELLED_STATUS_ID && !cancelReason.trim()) {
        setCancelReasonError(true);
        addToast(
          'Informe o motivo do cancelamento antes de gerar a etiqueta.',
          {
            appearance: 'error',
            autoDismiss: true,
          },
        );
        return;
      }

      if (itens.length === 0) {
        addToast(
          'O pedido deve ter ao menos um item antes de gerar a etiqueta.',
          {
            appearance: 'error',
            autoDismiss: true,
          },
        );
        return;
      }

      const invalidItem = itens.find(
        item =>
          !item.productId ||
          !item.colorId ||
          normalizeDecimalValue(item.qtde) <= 0,
      );

      if (invalidItem) {
        addToast('Salve apenas itens válidos antes de gerar a etiqueta.', {
          appearance: 'error',
          autoDismiss: true,
        });
        return;
      }

      try {
        const vols = volumes.replace(/\\/g, '');
        const info = JSON.parse(vols);

        setCancelReasonError(false);
        setLoadingEtiqueta(true);
        const updated = await persistOrderUpdate();

        if (!updated) {
          return;
        }

        const productsData = [];
        const volumesData = [];

        itens.forEach(item => {
          productsData.push({
            name: item.productName,
            quantity: normalizeDecimalValue(item.qtde) || 1,
            unitary_value: Number(item.unitValue || 0),
          });
        });

        info.forEach(volume => {
          volumesData.push({
            width: volume.dimensions.width,
            height: volume.dimensions.height,
            length: volume.dimensions.length,
            weight: volume.weight,
          });
        });

        const currentOrderTotal = itens.reduce((acc, item) => {
          return (
            acc + normalizeDecimalValue(item.qtde) * Number(item.unitValue || 0)
          );
        }, 0);

        const response = await api.post(`/orders/generatelabel/${data.id}`, {
          productsData,
          volumesData,
          customer: data.customer,
          address: data.address,
          order_id: data.id,
          nf,
          total: currentOrderTotal,
          frete: data.freteOpcao,
        });

        setUrl(response.data.url);
      } catch (e) {
        addToast(
          e.response?.data?.message ||
            'Ocorreu um erro. Verifique o saldo, a chave NFE e se a conta do Melhor Envio está autenticada.',
          {
            appearance: 'error',
            autoDismiss: true,
          },
        );
      } finally {
        setLoadingEtiqueta(false);
      }
    },
    [
      addToast,
      cancelReason,
      data,
      editingAddress,
      itens,
      nf,
      persistOrderUpdate,
      status,
    ],
  );

  const handleSelectProduct = useCallback(
    async option => {
      setSelectedProduct(option);
      setSelectedColorId('');
      setSelectedProductColors([]);

      if (!option) {
        setSelectedUnitValue(0);
        return;
      }

      try {
        setLoadingProductDetails(true);

        const productInfo = await fetchProductDetails(option.value);
        const colors = productInfo?.colorOptions || [];

        setSelectedProductColors(colors);
        setSelectedUnitValue(
          Number(productInfo?.unitValue || option.unitValue || 0),
        );

        if (colors.length > 0) {
          setSelectedColorId(colors[0].id);
        }
      } catch (e) {
        addToast('Não foi possível carregar as cores do produto selecionado.', {
          appearance: 'error',
          autoDismiss: true,
        });
      } finally {
        setLoadingProductDetails(false);
      }
    },
    [addToast, fetchProductDetails],
  );

  const handleAddItem = useCallback(async () => {
    if (!selectedProduct) {
      addToast('Selecione um produto para adicionar ao pedido.', {
        appearance: 'error',
        autoDismiss: true,
      });
      return;
    }

    const quantity = normalizeDecimalValue(selectedQuantity);

    if (quantity <= 0) {
      addToast('Informe uma quantidade válida.', {
        appearance: 'error',
        autoDismiss: true,
      });
      return;
    }

    try {
      const productInfo = await fetchProductDetails(selectedProduct.value);
      const colorOptions = productInfo?.colorOptions || selectedProductColors;
      const colorId = selectedColorId || colorOptions[0]?.id;

      if (!colorId) {
        addToast('Selecione uma cor para o item.', {
          appearance: 'error',
          autoDismiss: true,
        });
        return;
      }

      if (
        itens.find(
          item =>
            item.productId === selectedProduct.value &&
            item.colorId === colorId,
        )
      ) {
        addToast(
          'Este produto com a mesma cor já existe no pedido. Ajuste a quantidade na linha existente.',
          {
            appearance: 'error',
            autoDismiss: true,
          },
        );
        return;
      }

      const selectedColor = colorOptions.find(color => color.id === colorId);

      setItens(currentItems => [
        ...currentItems,
        {
          key: createItemKey(),
          productId: selectedProduct.value,
          productCode: productInfo?.code || selectedProduct.code,
          productName: productInfo?.name || selectedProduct.name,
          productLabel: buildProductLabel(
            productInfo?.code || selectedProduct.code,
            productInfo?.name || selectedProduct.name,
          ),
          colorId,
          colorName: selectedColor?.name || '',
          colorHex: selectedColor?.color || '',
          colorOptions,
          qtde: normalizeQuantityInput(selectedQuantity),
          unitValue: Number(
            productInfo?.unitValue || selectedProduct.unitValue || 0,
          ),
          discountValue: 0,
          couponId: null,
          couponCode: '-x-',
        },
      ]);

      setSelectedProduct(null);
      setSelectedProductColors([]);
      setSelectedColorId('');
      setSelectedQuantity('1');
      setSelectedUnitValue(0);
    } catch (e) {
      addToast('Ocorreu um erro ao adicionar o item ao pedido.', {
        appearance: 'error',
        autoDismiss: true,
      });
    }
  }, [
    addToast,
    fetchProductDetails,
    itens,
    selectedColorId,
    selectedProduct,
    selectedProductColors,
    selectedQuantity,
  ]);

  const handleRemoveItem = useCallback(itemKey => {
    setItens(currentItems => currentItems.filter(item => item.key !== itemKey));
  }, []);

  const handleItemQuantityChange = useCallback((itemKey, value) => {
    setItens(currentItems =>
      currentItems.map(item =>
        item.key === itemKey
          ? {
              ...item,
              qtde: value,
            }
          : item,
      ),
    );
  }, []);

  const handleItemColorChange = useCallback((itemKey, colorId) => {
    setItens(currentItems =>
      currentItems.map(item => {
        if (item.key !== itemKey) {
          return item;
        }

        const selectedColor = item.colorOptions.find(
          color => color.id === colorId,
        );

        return {
          ...item,
          colorId,
          colorName: selectedColor?.name || item.colorName,
          colorHex: selectedColor?.color || item.colorHex,
        };
      }),
    );
  }, []);

  const handleAddressChange = useCallback((field, value) => {
    setAddressDraft(currentAddress => ({
      ...buildEditableAddress(currentAddress),
      [field]: field === 'estado' ? value.toUpperCase().slice(0, 2) : value,
    }));
  }, []);

  const startAddressEdit = useCallback(() => {
    setAddressDraft(buildEditableAddress(data?.address));
    setEditingAddress(true);
  }, [data]);

  const cancelAddressEdit = useCallback(() => {
    setAddressDraft(buildEditableAddress(data?.address));
    setEditingAddress(false);
  }, [data]);

  const getItemsSubtotal = useCallback(() => {
    return getItemsSubtotalValue(itens);
  }, [itens]);

  const validateAddress = useCallback(
    addressPayload => {
      if (data?.freteEmpresa === 'X') {
        return true;
      }

      const missingField = REQUIRED_ADDRESS_FIELDS.find(
        ([field]) => !String(addressPayload?.[field] || '').trim(),
      );

      if (!missingField) {
        return true;
      }

      addToast(`Informe ${missingField[1]} antes de continuar.`, {
        appearance: 'error',
        autoDismiss: true,
      });

      return false;
    },
    [addToast, data],
  );

  const persistOrderUpdate = useCallback(async () => {
    const itemsSubtotal = getItemsSubtotal();
    const rawItems = itens.map(buildOrderUpdateItem);

    const payloadBase = {
      status_id: status,
      cancel_reason: status === CANCELLED_STATUS_ID ? cancelReason : undefined,
      nf,
      total: itemsSubtotal,
      freteValor: Number(data?.freteValor || 0),
      valordesconto: Number(data?.valordesconto || 0),
    };

    await api.patch(`/orders/update/${id}`, {
      ...payloadBase,
      itens: rawItems,
    });

    return true;
  }, [data, getItemsSubtotal, id, itens, nf, status, cancelReason]);

  const saveAddress = useCallback(async () => {
    const addressPayload = buildEditableAddress(addressDraft);

    if (!validateAddress(addressPayload)) {
      return;
    }

    try {
      setLoadingAddressSave(true);

      await api.patch(`/orders/update/address/${addressPayload.id}`, {
        identificacao: addressPayload.identificacao,
        endereco: addressPayload.endereco,
        numero: addressPayload.numero,
        complemento: addressPayload.complemento,
        bairro: addressPayload.bairro,
        cidade: addressPayload.cidade,
        estado: addressPayload.estado,
        cep: addressPayload.cep,
        itens: (data?.itens || []).map(buildOrderUpdateItem),
      });

      setData(currentData => {
        if (!currentData) {
          return currentData;
        }

        return {
          ...currentData,
          address: addressPayload,
        };
      });
      setAddressDraft(addressPayload);
      setEditingAddress(false);

      addToast('Endereço de entrega atualizado com sucesso.', {
        appearance: 'success',
        autoDismiss: true,
      });
    } catch (e) {
      addToast(
        e.response?.data?.message ||
          'Ocorreu um erro ao atualizar o endereço de entrega.',
        {
          appearance: 'error',
          autoDismiss: true,
        },
      );
    } finally {
      setLoadingAddressSave(false);
    }
  }, [addressDraft, addToast, data, id, validateAddress]);

  const saveOrder = useCallback(async () => {
    if (itens.length === 0) {
      addToast('O pedido deve ter ao menos um item.', {
        appearance: 'error',
        autoDismiss: true,
      });
      return;
    }

    const invalidItem = itens.find(
      item =>
        !item.productId ||
        !item.colorId ||
        normalizeDecimalValue(item.qtde) <= 0,
    );

    if (invalidItem) {
      addToast(
        'Revise os itens do pedido. Todos precisam ter produto, cor e quantidade válida.',
        {
          appearance: 'error',
          autoDismiss: true,
        },
      );
      return;
    }

    if (editingAddress) {
      addToast(
        'Salve ou cancele a edição do endereço antes de salvar o pedido.',
        {
          appearance: 'error',
          autoDismiss: true,
        },
      );
      return;
    }

    setCancelReasonError(false);

    try {
      setLoadingSave(true);

      const updated = await persistOrderUpdate();

      if (!updated) {
        return;
      }

      addToast('Pedido atualizado com sucesso.', {
        appearance: 'success',
        autoDismiss: true,
      });

      await loadData();
    } catch (e) {
      addToast(
        e.response?.data?.message || 'Ocorreu um erro ao salvar o pedido.',
        {
          appearance: 'error',
          autoDismiss: true,
        },
      );
    } finally {
      setLoadingSave(false);
    }
  }, [
    addToast,
    cancelReason,
    editingAddress,
    itens,
    loadData,
    persistOrderUpdate,
    status,
  ]);

  const updateStatus = useCallback(async () => {
    if (status === CANCELLED_STATUS_ID && !cancelReason.trim()) {
      setCancelReasonError(true);
      addToast('Informe o motivo do cancelamento.', {
        appearance: 'error',
        autoDismiss: true,
      });
      return;
    }

    setCancelReasonError(false);

    try {
      setLoadingSave(true);

      await api.patch(`/orders/${id}`, {
        status_id: status,
        cancel_reason: status === CANCELLED_STATUS_ID ? cancelReason : null,
      });

      addToast('Status do pedido atualizado com sucesso.', {
        appearance: 'success',
        autoDismiss: true,
      });

      await loadData();
    } catch (e) {
      addToast(
        e.response?.data?.message || 'Ocorreu um erro ao salvar o pedido.',
        {
          appearance: 'error',
          autoDismiss: true,
        },
      );
    } finally {
      setLoadingSave(false);
    }
  }, [addToast, cancelReason, itens, loadData, status]);

  return (
    <div>
      <GridContainer>
        <GridItem xs={12} sm={12} md={12}>
          <Card>
            <CardHeader color="primary">
              <h4 className={classes.cardTitleWhite}>Dados do pedido</h4>
            </CardHeader>
            <CardBody>
              {loading ? (
                <Box mt={6}>
                  <CircularProgress />
                </Box>
              ) : (
                <>
                  {trackingUrl && (
                    <>
                      <a
                        href={`${trackingUrl}`}
                        target="_blank"
                        style={{ textDecoration: 'none' }}
                      >
                        <Button color="secondary" type="button">
                          ACOMPANHAR PEDIDO
                        </Button>
                      </a>
                    </>
                  )}
                  <GridContainer>
                    <GridItem xs={12} sm={12} md={12}>
                      <TextField
                        variant="outlined"
                        margin="normal"
                        fullWidth
                        id="nopedido"
                        label="No Pedido"
                        name="nopedido"
                        disabled
                        value={data.id}
                        autoFocus
                      />
                    </GridItem>
                  </GridContainer>
                  <GridContainer>
                    <GridItem xs={12} sm={12} md={12}>
                      <TextField
                        variant="outlined"
                        margin="normal"
                        fullWidth
                        id="name"
                        label={
                          data.customer.type === 'PF' ? 'Nome' : 'Razão Social'
                        }
                        name="name"
                        disabled
                        value={data.customer.name}
                      />
                    </GridItem>
                  </GridContainer>
                  {data.customer.type === 'PJ' ? (
                    <GridContainer>
                      <GridItem xs={12} sm={12} md={12}>
                        <TextField
                          variant="outlined"
                          margin="normal"
                          fullWidth
                          id="cnpj"
                          label="CNPJ"
                          name="cnpj"
                          disabled
                          value={data.customer.document}
                        />
                      </GridItem>
                      <GridItem xs={12} sm={12} md={12}>
                        <TextField
                          variant="outlined"
                          margin="normal"
                          fullWidth
                          id="name"
                          label="Inscrição Estadual"
                          name="inscricao"
                          disabled
                          value={data.customer.inscricao}
                        />
                      </GridItem>
                    </GridContainer>
                  ) : null}
                  <GridContainer>
                    <GridItem xs={12} sm={12} md={12}>
                      <TextField
                        variant="outlined"
                        margin="normal"
                        fullWidth
                        id="name"
                        label="Email"
                        name="email"
                        disabled
                        value={data.customer.email}
                      />
                    </GridItem>
                  </GridContainer>
                  <GridContainer>
                    <GridItem xs={12} sm={12} md={4}>
                      <TextField
                        variant="outlined"
                        margin="normal"
                        fullWidth
                        id="telefone"
                        label="Telefone 1"
                        name="telefone"
                        disabled
                        value={data.customer.tel_01}
                      />
                    </GridItem>
                    <GridItem xs={12} sm={12} md={4}>
                      <TextField
                        variant="outlined"
                        margin="normal"
                        fullWidth
                        id="telefone"
                        label="Telefone 2"
                        name="telefone"
                        disabled
                        value={data.customer.tel_02}
                      />
                    </GridItem>
                    <GridItem xs={12} sm={12} md={4}>
                      <TextField
                        variant="outlined"
                        margin="normal"
                        fullWidth
                        id="telefone"
                        label="Telefone 3"
                        name="telefone"
                        disabled
                        value={data.customer.tel_03}
                      />
                    </GridItem>
                  </GridContainer>
                  <GridContainer>
                    <GridItem xs={12} sm={12} md={12}>
                      <TextField
                        variant="outlined"
                        margin="normal"
                        fullWidth
                        id="formapg"
                        label="Forma de pagamento"
                        name="formapg"
                        disabled
                        value={data.paymentform.name}
                      />
                    </GridItem>
                  </GridContainer>
                  <GridContainer>
                    <GridItem xs={12} sm={12} md={12}>
                      <TextField
                        variant="outlined"
                        margin="normal"
                        fullWidth
                        id="total"
                        label="Total"
                        name="total"
                        disabled
                        value={total}
                      />
                    </GridItem>
                  </GridContainer>
                  <GridContainer>
                    <GridItem xs={12} sm={12} md={12}>
                      <TextField
                        variant="outlined"
                        margin="normal"
                        fullWidth
                        id="cadastrado"
                        label="Cadastrado em"
                        name="cadastrado"
                        disabled
                        value={new Date(data.created_at).toLocaleDateString(
                          'pt-br',
                          {
                            day: '2-digit',
                            month: 'long',
                            year: 'numeric',
                          },
                        )}
                      />
                    </GridItem>
                  </GridContainer>
                  <h3>Endereço para entrega</h3>
                  {!editingAddress ? (
                    <div style={{ marginTop: 20, marginBottom: 20 }}>
                      <p style={{ margin: 0 }}>
                        <strong>
                          {data.address.identificacao || 'Sem identificação'}
                        </strong>
                      </p>
                      <p style={{ marginTop: 8, marginBottom: 16 }}>
                        {data.address.endereco} {data.address.numero}{' '}
                        {data.address.complemento} {data.address.bairro}
                        <br />
                        {data.address.cidade} {data.address.estado} CEP:{' '}
                        {data.address.cep}
                      </p>
                      <Button
                        color="info"
                        type="button"
                        onClick={startAddressEdit}
                      >
                        Alterar endereço de entrega
                      </Button>
                    </div>
                  ) : (
                    <>
                      <GridContainer>
                        <GridItem xs={12} sm={12} md={12}>
                          <TextField
                            variant="outlined"
                            margin="normal"
                            fullWidth
                            id="address-identificacao"
                            label="Identificação"
                            name="address-identificacao"
                            value={addressDraft.identificacao}
                            onChange={e =>
                              handleAddressChange(
                                'identificacao',
                                e.target.value,
                              )
                            }
                          />
                        </GridItem>
                      </GridContainer>
                      <GridContainer>
                        <GridItem xs={12} sm={12} md={4}>
                          <TextField
                            variant="outlined"
                            margin="normal"
                            fullWidth
                            id="address-cep"
                            label="CEP"
                            name="address-cep"
                            value={addressDraft.cep}
                            onChange={e =>
                              handleAddressChange('cep', e.target.value)
                            }
                          />
                        </GridItem>
                        <GridItem xs={12} sm={12} md={8}>
                          <TextField
                            variant="outlined"
                            margin="normal"
                            fullWidth
                            id="address-endereco"
                            label="Endereço"
                            name="address-endereco"
                            value={addressDraft.endereco}
                            onChange={e =>
                              handleAddressChange('endereco', e.target.value)
                            }
                          />
                        </GridItem>
                      </GridContainer>
                      <GridContainer>
                        <GridItem xs={12} sm={12} md={3}>
                          <TextField
                            variant="outlined"
                            margin="normal"
                            fullWidth
                            id="address-numero"
                            label="Número"
                            name="address-numero"
                            value={addressDraft.numero}
                            onChange={e =>
                              handleAddressChange('numero', e.target.value)
                            }
                          />
                        </GridItem>
                        <GridItem xs={12} sm={12} md={9}>
                          <TextField
                            variant="outlined"
                            margin="normal"
                            fullWidth
                            id="address-complemento"
                            label="Complemento"
                            name="address-complemento"
                            value={addressDraft.complemento}
                            onChange={e =>
                              handleAddressChange('complemento', e.target.value)
                            }
                          />
                        </GridItem>
                      </GridContainer>
                      <GridContainer>
                        <GridItem xs={12} sm={12} md={4}>
                          <TextField
                            variant="outlined"
                            margin="normal"
                            fullWidth
                            id="address-bairro"
                            label="Bairro"
                            name="address-bairro"
                            value={addressDraft.bairro}
                            onChange={e =>
                              handleAddressChange('bairro', e.target.value)
                            }
                          />
                        </GridItem>
                        <GridItem xs={12} sm={12} md={5}>
                          <TextField
                            variant="outlined"
                            margin="normal"
                            fullWidth
                            id="address-cidade"
                            label="Cidade"
                            name="address-cidade"
                            value={addressDraft.cidade}
                            onChange={e =>
                              handleAddressChange('cidade', e.target.value)
                            }
                          />
                        </GridItem>
                        <GridItem xs={12} sm={12} md={3}>
                          <TextField
                            variant="outlined"
                            margin="normal"
                            fullWidth
                            id="address-estado"
                            label="Estado"
                            name="address-estado"
                            value={addressDraft.estado}
                            onChange={e =>
                              handleAddressChange('estado', e.target.value)
                            }
                            inputProps={{ maxLength: 2 }}
                          />
                        </GridItem>
                      </GridContainer>
                      <div style={{ marginTop: 20, marginBottom: 20 }}>
                        <Button
                          color="warning"
                          type="button"
                          onClick={saveAddress}
                          disabled={loadingAddressSave}
                        >
                          {loadingAddressSave
                            ? 'Salvando endereço...'
                            : 'Salvar endereço de entrega'}
                        </Button>
                        <Button
                          color="primary"
                          type="button"
                          style={{ marginLeft: 10 }}
                          onClick={cancelAddressEdit}
                          disabled={loadingAddressSave}
                        >
                          Cancelar
                        </Button>
                      </div>
                    </>
                  )}
                  <h3>Status do pedido</h3>
                  <FormControl
                    fullWidth
                    variant="outlined"
                    className={classes.formControl}
                    style={{ marginTop: 25 }}
                  >
                    <InputLabel id="ordem_label">
                      Status atual do pedido:
                    </InputLabel>
                    <Select
                      labelId="status"
                      label="Status Atual do Pedido"
                      variant="outlined"
                      name="status"
                      fullWidth
                      value={status}
                      onChange={e => setStatus(e.target.value)}
                    >
                      {dataStatus &&
                        dataStatus.map(item => (
                          <MenuItem value={item.id} key={item.id}>
                            <em>{item.name}</em>
                          </MenuItem>
                        ))}
                    </Select>
                  </FormControl>
                  {status === CANCELLED_STATUS_ID && (
                    <TextField
                      variant="outlined"
                      margin="normal"
                      fullWidth
                      id="cancelReason"
                      label="Motivo do cancelamento"
                      name="cancelReason"
                      value={cancelReason}
                      onChange={e => setCancelReason(e.target.value)}
                      error={cancelReasonError}
                      helperText={
                        cancelReasonError ? 'Motivo é obrigatório.' : ''
                      }
                    />
                  )}
                  <Button
                    color="warning"
                    type="button"
                    onClick={updateStatus}
                    disabled={loadingSave}
                  >
                    {loadingSave
                      ? 'Salvando alterações...'
                      : 'Atualizar Status'}
                  </Button>
                  <h3>Informações adicionais</h3>
                  {data.boleto_url ? (
                    <p>
                      <strong>Boleto: </strong>{' '}
                      <a
                        href={data.boleto_url}
                        target="_blank"
                        rel="noreferrer"
                      >
                        {data.boleto_url}
                      </a>
                    </p>
                  ) : null}
                  {data.brand ? (
                    <p>
                      <strong>Cartão: </strong> {data.brand} -{' '}
                      {data.last_digits}
                    </p>
                  ) : null}
                  <p>
                    <strong>Id transação Pagar.Me: </strong>{' '}
                    {data.transaction_id}
                  </p>
                  {data.parcelas ? (
                    <p>
                      <strong>No Parcelas: </strong> {data.parcelas}
                    </p>
                  ) : null}
                  {data.nsu ? (
                    <p>
                      <strong>NSU: </strong> {data.nsu}
                    </p>
                  ) : null}
                  {data.authcode ? (
                    <p>
                      <strong>COD AUTORIZAÇÃO: </strong> {data.authcode}
                    </p>
                  ) : null}
                  {data.finished_at ? (
                    <p>
                      <strong>Pedido finalizado em: </strong>{' '}
                      {new Date(data.finished_at).toLocaleDateString('pt-br', {
                        day: '2-digit',
                        month: 'long',
                        year: 'numeric',
                      })}
                    </p>
                  ) : null}
                  <h3>Itens do pedido</h3>
                  {itens.length > 0 ? (
                    <div style={{ overflowX: 'auto', marginTop: 20 }}>
                      <MuiTable>
                        <TableHead>
                          <TableRow>
                            <TableCell>Produto</TableCell>
                            <TableCell>Cor</TableCell>
                            <TableCell>Qtde</TableCell>
                            <TableCell>Valor</TableCell>
                            <TableCell>Subtotal</TableCell>
                            <TableCell>Desconto</TableCell>
                            <TableCell>Cupom</TableCell>
                            <TableCell />
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {itens.map(item => (
                            <TableRow key={item.key}>
                              <TableCell style={{ minWidth: 280 }}>
                                {item.productLabel}
                              </TableCell>
                              <TableCell style={{ minWidth: 220 }}>
                                <FormControl
                                  fullWidth
                                  variant="outlined"
                                  size="small"
                                >
                                  <Select
                                    value={item.colorId}
                                    onChange={event =>
                                      handleItemColorChange(
                                        item.key,
                                        event.target.value,
                                      )
                                    }
                                  >
                                    {item.colorOptions.map(color => (
                                      <MenuItem key={color.id} value={color.id}>
                                        <div
                                          style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                          }}
                                        >
                                          <div
                                            style={{
                                              width: 14,
                                              height: 14,
                                              borderRadius: '50%',
                                              backgroundColor:
                                                color.color || '#ccc',
                                              marginRight: 8,
                                              border: '1px solid #ddd',
                                            }}
                                          />
                                          {color.name}
                                        </div>
                                      </MenuItem>
                                    ))}
                                  </Select>
                                </FormControl>
                              </TableCell>
                              <TableCell style={{ minWidth: 140 }}>
                                <CurrencyTextField
                                  label=""
                                  currencySymbol=""
                                  variant="outlined"
                                  margin="dense"
                                  fullWidth
                                  textAlign="left"
                                  outputFormat="string"
                                  decimalCharacter=","
                                  decimalCharacterAlternative="."
                                  decimalPlaces={4}
                                  decimalPlacesShownOnBlur={4}
                                  decimalPlacesShownOnFocus={4}
                                  digitGroupSeparator=""
                                  value={item.qtde}
                                  onChange={(event, value) =>
                                    handleItemQuantityChange(item.key, value)
                                  }
                                />
                              </TableCell>
                              <TableCell>
                                {formatCurrency(item.unitValue)}
                              </TableCell>
                              <TableCell>
                                {formatCurrency(
                                  normalizeDecimalValue(item.qtde) *
                                    Number(item.unitValue || 0),
                                )}
                              </TableCell>
                              <TableCell>
                                {formatCurrency(item.discountValue)}
                              </TableCell>
                              <TableCell>{item.couponCode}</TableCell>
                              <TableCell align="right">
                                <IconButton
                                  aria-label="Remover item"
                                  onClick={() => handleRemoveItem(item.key)}
                                >
                                  <Delete fontSize="small" />
                                </IconButton>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </MuiTable>
                    </div>
                  ) : (
                    <Box
                      bgcolor="text.primary"
                      color="warning.contrastText"
                      p={2}
                      style={{ marginTop: 20 }}
                    >
                      <p style={{ color: '#FFF', margin: 0 }}>
                        Nenhum item cadastrado ao pedido.
                      </p>
                    </Box>
                  )}
                  <h4 style={{ marginTop: 30 }}>Adicionar novo item</h4>
                  <GridContainer>
                    <GridItem xs={12} sm={12} md={5}>
                      <p style={{ marginBottom: 8 }}>
                        <strong>Produto</strong>
                      </p>
                      <ReactSelect
                        styles={selectStyles}
                        menuPortalTarget={document.body}
                        menuPosition="fixed"
                        options={productOptions}
                        isLoading={loadingProducts}
                        placeholder="Selecione um produto"
                        noOptionsMessage={() =>
                          loadingProducts
                            ? 'Carregando produtos...'
                            : 'Nenhum produto encontrado'
                        }
                        value={selectedProduct}
                        onChange={handleSelectProduct}
                      />
                      {selectedProduct ? (
                        <p style={{ marginTop: 8, marginBottom: 0 }}>
                          Valor unitário atual:{' '}
                          {formatCurrency(selectedUnitValue)}
                        </p>
                      ) : null}
                    </GridItem>
                    <GridItem xs={12} sm={12} md={3}>
                      <FormControl
                        fullWidth
                        variant="outlined"
                        className={classes.formControl}
                        style={{ marginTop: 24 }}
                      >
                        <InputLabel id="new-color-label">Cor</InputLabel>
                        <Select
                          labelId="new-color-label"
                          label="Cor"
                          value={selectedColorId}
                          disabled={
                            !selectedProduct ||
                            loadingProductDetails ||
                            selectedProductColors.length === 0
                          }
                          onChange={event =>
                            setSelectedColorId(event.target.value)
                          }
                        >
                          {selectedProductColors.map(color => (
                            <MenuItem key={color.id} value={color.id}>
                              <div
                                style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                }}
                              >
                                <div
                                  style={{
                                    width: 14,
                                    height: 14,
                                    borderRadius: '50%',
                                    backgroundColor: color.color || '#ccc',
                                    marginRight: 8,
                                    border: '1px solid #ddd',
                                  }}
                                />
                                {color.name}
                              </div>
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </GridItem>
                    <GridItem xs={12} sm={12} md={2}>
                      <CurrencyTextField
                        style={{ marginTop: 24 }}
                        label="Quantidade"
                        currencySymbol=""
                        variant="outlined"
                        margin="normal"
                        fullWidth
                        textAlign="left"
                        outputFormat="string"
                        decimalCharacter=","
                        decimalCharacterAlternative="."
                        decimalPlaces={4}
                        decimalPlacesShownOnBlur={4}
                        decimalPlacesShownOnFocus={4}
                        digitGroupSeparator=""
                        value={selectedQuantity}
                        onChange={(event, value) => setSelectedQuantity(value)}
                      />
                    </GridItem>
                    <GridItem
                      xs={12}
                      sm={12}
                      md={2}
                      style={{ display: 'flex', alignItems: 'flex-end' }}
                    >
                      <Button
                        color="info"
                        type="button"
                        style={{
                          width: '100%',
                          minHeight: 56,
                          marginBottom: 8,
                        }}
                        onClick={handleAddItem}
                        disabled={loadingProductDetails}
                      >
                        Adicionar
                      </Button>
                    </GridItem>
                  </GridContainer>
                  <div style={{ marginTop: 20 }}>
                    <Button
                      color="warning"
                      type="button"
                      onClick={saveOrder}
                      disabled={loadingSave}
                    >
                      {loadingSave
                        ? 'Salvando alterações...'
                        : 'Salvar alterações do pedido'}
                    </Button>
                  </div>
                  <hr />
                  <h4>Frete: </h4>
                  <br />
                  {data.freteValor > 0 ? (
                    <>
                      <p>
                        <strong>Opção:</strong> {data.freteOpcao}
                      </p>
                      <p>
                        <strong>Empresa:</strong> {data.freteEmpresa}
                      </p>
                      <p>
                        <strong>Previsão:</strong> {data.fretePrevisao} dd
                      </p>
                      <p>
                        <strong>Valor Frete:</strong>
                      </p>

                      <h4>(+) {formatCurrency(data.freteValor)}</h4>
                    </>
                  ) : (
                    <>
                      {data.freteEmpresa === 'X' ? (
                        <p>Retirar na loja</p>
                      ) : (
                        <p>Frete Grátis</p>
                      )}
                    </>
                  )}
                  {data.tipodesconto ? (
                    <>
                      <hr />
                      <h4>Descontos: </h4>
                      <br />
                      <p>
                        <strong>{data.tipodesconto}</strong>
                      </p>
                      <h4>(-) {formatCurrency(data.valordesconto)}</h4>

                      <hr />

                      <h3>Valor recebido: {total}</h3>
                    </>
                  ) : null}
                  <hr />
                  {data.freteValor > 0 ? (
                    <div>
                      <TextField
                        variant="outlined"
                        margin="normal"
                        fullWidth
                        id="nf"
                        label="Chave NFE"
                        name="nf"
                        value={nf}
                        onChange={e => setNF(e.target.value)}
                      />

                      {!loadingMelhorEnvioStatus &&
                      melhorEnvioConnected === false ? (
                        <div style={{ marginBottom: 16 }}>
                          <Button
                            color="warning"
                            type="button"
                            onClick={autenticarMelhorEnvio}
                          >
                            Re-conectar com o Melhor Envio
                          </Button>
                        </div>
                      ) : null}

                      {!url ? (
                        <div>
                          {!loadingEtiqueta ? (
                            <Button
                              color="info"
                              type="button"
                              onClick={() => gerarEtiqueta(data.volumes)}
                            >
                              Comprar / Gerar Etiqueta - Melhor Envio
                            </Button>
                          ) : (
                            <Box mt={6}>
                              <CircularProgress />
                            </Box>
                          )}
                        </div>
                      ) : null}
                    </div>
                  ) : null}
                  {url && !loadingEtiqueta ? (
                    <a href={url} target="_blank" rel="noreferrer">
                      <Button color="danger" type="button">
                        Imprimir Etiqueta
                      </Button>
                    </a>
                  ) : null}
                  <hr />
                </>
              )}
            </CardBody>
            <CardFooter>
              <Link to="/admin/orders">
                <Button color="primary" type="button">
                  Voltar
                </Button>
              </Link>
            </CardFooter>
          </Card>
        </GridItem>
      </GridContainer>
    </div>
  );
}
